import React, { useCallback, useEffect, useRef, useState } from 'react';
import cashReservesService from '../../services/cashReservesService';
import { DIFF_FIELD_LABELS, fmtCurrencyPrecise } from './helpers';

/**
 * One account's retired reserves, with the controls to combine them.
 *
 * Purely presentational — the disclosure below owns the fetch. Editing a
 * reserve in Tamarac changes its fingerprint, so the same reserve appears as a
 * retired ID plus a new one. Combining is always a human action; nothing is
 * merged automatically.
 */
const ReserveLineageList = ({ accountNumber, accountName, showAccount, data, onReload, onCombined }) => {
  const [error, setError] = useState('');
  const [workingId, setWorkingId] = useState('');
  const [selection, setSelection] = useState({}); // retired id -> chosen current id

  const retired = data?.retired || [];
  if (!error && retired.length === 0) return null;

  const suggestionFor = (retiredId) =>
    (data?.suggestions || []).find((s) => s.retired_reserve_id === retiredId);
  const currentLabel = (reserveId) => {
    const entry = (data?.current || []).find((r) => r.reserve_id === reserveId);
    return entry
      ? `${entry.reserve_category || 'Uncategorized'} (${fmtCurrencyPrecise(entry.calculated_reserve_dollar)})`
      : reserveId;
  };

  const runAction = async (retiredId, action, failureMessage) => {
    setWorkingId(retiredId);
    setError('');
    try {
      await action();
      await onReload(accountNumber);
      // Combining changes the linked_reserves labels shown on transactions, so
      // the caller invalidates that account's cached transactions too.
      if (onCombined) onCombined(accountNumber);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || failureMessage);
    } finally {
      setWorkingId('');
    }
  };

  const combine = (retiredId) => {
    const target = selection[retiredId] ?? suggestionFor(retiredId)?.current_reserve_id;
    if (!target) return;
    return runAction(
      retiredId,
      () =>
        cashReservesService.saveReserveIdLink(accountNumber, {
          retired_reserve_id: retiredId,
          current_reserve_id: target,
        }),
      'Failed to combine reserves'
    );
  };

  const unlink = (retiredId) =>
    runAction(
      retiredId,
      () => cashReservesService.deleteReserveIdLink(accountNumber, retiredId),
      'Failed to unlink reserves'
    );

  return (
    <div className="mb-2">
      {showAccount && (
        <div className="fw-semibold mb-1" title={`${accountName} · ${accountNumber}`}>
          {accountName || accountNumber}
        </div>
      )}
      {error && (
        <div className="alert alert-danger py-1 px-2 small" role="alert">
          {error}
        </div>
      )}
      <ul className="list-unstyled mb-0 small">
        {retired.map((row) => {
          const suggestion = suggestionFor(row.reserve_id);
          const selected = selection[row.reserve_id] ?? suggestion?.current_reserve_id ?? '';
          return (
            <li key={row.reserve_id} className="d-flex flex-wrap align-items-center gap-2 mb-1">
              <span className="badge bg-light text-secondary border">
                Retired {row.retired_on || '—'}
              </span>
              <span className="fw-semibold">
                {row.reserve_category || 'Uncategorized'}
              </span>
              <span className="text-muted">{fmtCurrencyPrecise(row.reserve_amount)}</span>
              {row.linked_to ? (
                <>
                  <span className="text-muted">
                    combined into <span className="fw-semibold">{currentLabel(row.linked_to)}</span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger py-0"
                    onClick={() => unlink(row.reserve_id)}
                    disabled={workingId === row.reserve_id}
                  >
                    Unlink
                  </button>
                </>
              ) : (
                <>
                  {suggestion && (
                    <span
                      className="badge bg-info-subtle text-info-emphasis border"
                      title="Definitions differ on only these fields"
                    >
                      <i className="bi bi-lightbulb me-1"></i>
                      Likely edit — differs:{' '}
                      {suggestion.differences.map((f) => DIFF_FIELD_LABELS[f] || f).join(', ')}
                    </span>
                  )}
                  <select
                    className="form-select form-select-sm d-inline-block"
                    style={{ maxWidth: '280px' }}
                    aria-label="Combine into reserve"
                    value={selected}
                    onChange={(e) =>
                      setSelection((prev) => ({ ...prev, [row.reserve_id]: e.target.value }))
                    }
                  >
                    <option value="">Combine into…</option>
                    {(data?.current || []).map((entry) => (
                      <option key={entry.reserve_id} value={entry.reserve_id}>
                        {entry.reserve_category || 'Uncategorized'} (
                        {fmtCurrencyPrecise(entry.calculated_reserve_dollar)})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary py-0"
                    onClick={() => combine(row.reserve_id)}
                    disabled={!selected || workingId === row.reserve_id}
                  >
                    {workingId === row.reserve_id ? 'Combining…' : 'Combine'}
                  </button>
                </>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/**
 * Collapsed "Reserve Changes" section that fetches only when opened.
 *
 * Nearly every account has retired reserve fingerprints, so auto-loading this
 * for a whole family would fire one request per account on every expand and
 * stack up a wall of rows. The fetch lives here rather than in the list so the
 * section can tell the difference between "nothing retired" and "not loaded" —
 * a list that renders nothing can't report that upward.
 *
 * `accounts` is the FETCH scope, `visibleAccounts` the RENDER scope. There is
 * no cache here and the effect keys on the account list, so filtering the
 * fetched list would refire every request and discard the results already in
 * hand each time the user ticks a box.
 */
const ReserveLineageDisclosure = ({ accounts, visibleAccounts, onCombined }) => {
  const [open, setOpen] = useState(false);
  const [byAccount, setByAccount] = useState(null); // null = not loaded yet
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const requestIdRef = useRef(0);

  // The accounts array is rebuilt on every filter/sort/reload, so depending on
  // its identity would refetch forever.
  const accountKey = accounts.map((a) => a.account_number).join(',');
  const shown = visibleAccounts || accounts;
  const showAccount = shown.length > 1;

  const loadOne = useCallback(async (accountNumber) => {
    const result = await cashReservesService.getReserveLineage(accountNumber);
    setByAccount((prev) => ({ ...(prev || {}), [accountNumber]: result }));
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const requestId = (requestIdRef.current += 1);
    const isCurrent = () => requestId === requestIdRef.current;
    setIsLoading(true);
    setError('');
    const numbers = accountKey ? accountKey.split(',') : [];
    Promise.allSettled(numbers.map((n) => cashReservesService.getReserveLineage(n)))
      .then((results) => {
        if (!isCurrent()) return;
        const next = {};
        let failures = 0;
        results.forEach((result, i) => {
          if (result.status === 'fulfilled') next[numbers[i]] = result.value;
          else failures += 1;
        });
        setByAccount(next);
        if (failures) setError(`Could not load reserve history for ${failures} account(s).`);
      })
      .finally(() => {
        if (isCurrent()) setIsLoading(false);
      });
    return () => {
      requestIdRef.current += 1;
    };
  }, [open, accountKey]);

  const lists = shown
    .map((a) => ({ account: a, data: byAccount?.[a.account_number] }))
    .filter((entry) => (entry.data?.retired || []).length > 0);

  return (
    <div className="mb-3">
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-decoration-none"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`}></i>
        <span className="text-uppercase small" style={{ letterSpacing: '0.05em' }}>
          Reserve Changes
        </span>
      </button>
      {open && (
        <div className="mt-2">
          <div className="text-muted small mb-2">
            These reserves disappeared from Tamarac — usually deleted, or edited (which gives them
            a new identity here). Combine an edited reserve with its successor so history isn't
            double counted.
          </div>
          {error && (
            <div className="alert alert-danger py-1 px-2 small" role="alert">
              {error}
            </div>
          )}
          {isLoading ? (
            <div className="text-muted small">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status" />
              Loading reserve history…
            </div>
          ) : lists.length === 0 ? (
            <div className="text-muted small">No reserve changes to review.</div>
          ) : (
            lists.map(({ account, data }) => (
              <ReserveLineageList
                key={account.account_number}
                accountNumber={account.account_number}
                accountName={account.account_name}
                showAccount={showAccount}
                data={data}
                onReload={loadOne}
                onCombined={onCombined}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ReserveLineageDisclosure;
